import React from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Shield, User, Award, Building2, MapPin, CheckSquare, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { CATEGORIES_EXPERTISE } from './expertsValidationShared';

export const ExpertDialogs = ({
  openDetailModal, setOpenDetailModal, openDialog, setOpenDialog,
  selectedExpert, checklist, setChecklist, action, rejectionReason, setRejectionReason,
  validatingId, handleValidation, handleOpenDialog,
}) => {
  return (
    <>
      <Dialog open={openDetailModal} onOpenChange={setOpenDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-600" />
              Détails de l'Expert
            </DialogTitle>
            <DialogDescription>
              Vérification complète avant validation
            </DialogDescription>
          </DialogHeader>

          {selectedExpert && (
            <div className="space-y-6 py-4">
              {/* Identité */}
              <div>
                <h4 className="font-bold mb-3 flex items-center gap-2 text-purple-900">
                  <User className="h-4 w-4" />
                  Identité
                </h4>
                <div className="grid grid-cols-2 gap-4 bg-purple-50 p-4 rounded-lg">
                  <div>
                    <Label className="text-xs text-slate-600">Nom</Label>
                    <p className="font-semibold">{selectedExpert.nom}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Prénom</Label>
                    <p className="font-semibold">{selectedExpert.prenom}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Email</Label>
                    <p className="font-mono text-sm">{selectedExpert.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Téléphone</Label>
                    <p className="font-mono text-sm">{selectedExpert.telephone}</p>
                  </div>
                </div>
              </div>

              {/* Qualifications */}
              <div>
                <h4 className="font-bold mb-3 flex items-center gap-2 text-blue-900">
                  <Award className="h-4 w-4" />
                  Qualifications
                </h4>
                <div className="space-y-3 bg-blue-50 p-4 rounded-lg">
                  {selectedExpert.numero_agrement && (
                    <div>
                      <Label className="text-xs text-slate-600">N° Agrément</Label>
                      <p className="font-semibold">{selectedExpert.numero_agrement}</p>
                    </div>
                  )}
                  {selectedExpert.categories_expertise?.length > 0 && (
                    <div>
                      <Label className="text-xs text-slate-600 mb-2 block">Catégories d'expertise</Label>
                      <div className="flex flex-wrap gap-2">
                        {selectedExpert.categories_expertise.map(cat => {
                          const catInfo = CATEGORIES_EXPERTISE.find(c => c.value === cat);
                          return catInfo ? (
                            <Badge key={cat} className={`${catInfo.color} text-white`}>
                              {catInfo.icon} {catInfo.label}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  {selectedExpert.specialites?.length > 0 && (
                    <div>
                      <Label className="text-xs text-slate-600 mb-2 block">Spécialités</Label>
                      <div className="flex flex-wrap gap-2">
                        {selectedExpert.specialites.map((spec, idx) => (
                          <Badge key={idx} className="bg-purple-600 text-white font-semibold">{spec}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedExpert.experience_annees && (
                    <div>
                      <Label className="text-xs text-slate-600">Années d'expérience</Label>
                      <p className="font-semibold">{selectedExpert.experience_annees} ans</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Entreprise */}
              {selectedExpert.raison_sociale && (
                <div>
                  <h4 className="font-bold mb-3 flex items-center gap-2 text-green-900">
                    <Building2 className="h-4 w-4" />
                    Entreprise
                  </h4>
                  <div className="space-y-3 bg-green-50 p-4 rounded-lg">
                    <div>
                      <Label className="text-xs text-slate-600">Raison sociale</Label>
                      <p className="font-semibold">{selectedExpert.raison_sociale}</p>
                    </div>
                    {selectedExpert.siret && (
                      <div>
                        <Label className="text-xs text-slate-600">SIRET</Label>
                        <p className="font-mono font-semibold">{selectedExpert.siret}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Adresse */}
              <div>
                <h4 className="font-bold mb-3 flex items-center gap-2 text-orange-900">
                  <MapPin className="h-4 w-4" />
                  Adresse
                </h4>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm">
                    {selectedExpert.adresse}<br />
                    {selectedExpert.code_postal} {selectedExpert.ville}<br />
                    {selectedExpert.pays}
                  </p>
                </div>
              </div>

              {/* Checklist */}
              <div>
                <h4 className="font-bold mb-3 flex items-center gap-2 text-red-900">
                  <CheckSquare className="h-4 w-4" />
                  Checklist de Validation
                </h4>
                <div className="space-y-3 bg-red-50 p-4 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="qualif"
                      checked={checklist.qualifications_verifiees}
                      onCheckedChange={(checked) => setChecklist({...checklist, qualifications_verifiees: checked})}
                    />
                    <Label htmlFor="qualif" className="cursor-pointer flex-1">
                      Qualifications et habilitation vérifiées
                    </Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="exp"
                      checked={checklist.experience_adequate}
                      onCheckedChange={(checked) => setChecklist({...checklist, experience_adequate: checked})}
                    />
                    <Label htmlFor="exp" className="cursor-pointer flex-1">
                      Expérience adéquate pour les catégories sélectionnées
                    </Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="docs"
                      checked={checklist.documents_valides}
                      onCheckedChange={(checked) => setChecklist({...checklist, documents_valides: checked})}
                    />
                    <Label htmlFor="docs" className="cursor-pointer flex-1">
                      Documents justificatifs valides
                    </Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="refs"
                      checked={checklist.references_verifiees}
                      onCheckedChange={(checked) => setChecklist({...checklist, references_verifiees: checked})}
                    />
                    <Label htmlFor="refs" className="cursor-pointer flex-1">
                      Références professionnelles vérifiées
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenDetailModal(false)}>
              Fermer
            </Button>
            <Button
              onClick={() => {
                setOpenDetailModal(false);
                handleOpenDialog(selectedExpert, 'reject');
              }}
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Rejeter
            </Button>
            <Button
              onClick={() => {
                setOpenDetailModal(false);
                handleOpenDialog(selectedExpert, 'validate');
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmation */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {action === 'validate' ? '✅ Valider l\'expert' : '❌ Rejeter l\'expert'}
            </DialogTitle>
            <DialogDescription>
              {action === 'validate'
                ? 'Vérifiez que tous les critères sont validés'
                : 'Indiquez la raison du rejet'}
            </DialogDescription>
          </DialogHeader>

          {selectedExpert && (
            <div className="space-y-4 py-4">
              <div className="text-sm bg-slate-50 p-3 rounded">
                <p className="font-semibold">
                  {selectedExpert.prenom} {selectedExpert.nom}
                </p>
                <p className="text-slate-600">{selectedExpert.email}</p>
              </div>

              {action === 'validate' && (
                <div className="bg-green-50 border border-green-200 p-4 rounded">
                  <p className="text-sm font-semibold mb-2">Checklist obligatoire :</p>
                  {Object.values(checklist).every(v => v) ? (
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Tous les critères sont validés</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-orange-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Veuillez cocher tous les critères</span>
                    </div>
                  )}
                </div>
              )}

              {action === 'reject' && (
                <div>
                  <Label htmlFor="rejection-reason">Raison du rejet *</Label>
                  <Textarea
                    id="rejection-reason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Ex: Qualifications insuffisantes, documents manquants..."
                    rows={4}
                    className="mt-2"
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)} disabled={validatingId}>
              Annuler
            </Button>
            <Button
              onClick={handleValidation}
              disabled={validatingId}
              className={action === 'validate' ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={action === 'reject' ? 'destructive' : 'default'}
            >
              {validatingId ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Traitement...
                </>
              ) : (
                <>
                  {action === 'validate' ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                  {action === 'validate' ? 'Valider' : 'Rejeter'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
